import * as joint from "@clientio/rappid";
import { Region } from "../mvc/region";
import { State } from "../mvc/state";
import RappidContext from "./rappid-context";
import { flatten } from "lodash-es";
import { actionState } from "./action-state";
import { activeView, updateVisibleStates } from "../subdiagram-viewer";
import { redo, undo } from "./undo-redo";
import { MessageEventBus } from "../../event-bus";

export default class KeyboardController {
  constructor(private context: RappidContext) {}

  activate() {
    this.enable();
  }

  activateKeyboard() {
    const { keyboard, clipboard, selection, graph, commandManager, paperScroller, paper } = this.context;
    keyboard.on(
      {
        "ctrl+c command+c": () => {
          if (this.hasTextSelection()) return;
          clipboard.copyElements(this.getDeepSelection(), graph);
        },
        "ctrl+x command+x shift+delete": () => {
          if (this.hasTextSelection()) return;
          clipboard.cutElements(this.getDeepSelection(), graph);
        },
        "ctrl+v command+v": () => {
          const { allowed, message } = this.canPaste();
          if (!allowed) {
            MessageEventBus.emit("warning", {
              text: message,
              id: "statechart-editor",
            });
            return;
          }

          const pastedCells = clipboard.pasteCells(graph, {
            translate: { dx: 20, dy: 20 },
            useLocalStorage: true,
          });

          this.paste(pastedCells);

          const elements = pastedCells.filter((cell) => cell.isElement());
          selection.collection.reset(elements);
        },
        "ctrl+z command+z": () => {
          if (this.context.opt?.customUndo) {
            return;
          }
          undo(this.context);
        },
        "ctrl+y command+shift+z": () => {
          if (this.context.opt?.customRedo) {
            return;
          }
          redo(this.context);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "ctrl+a command+a": (evt: any) => {
          evt.preventDefault();
          selection.collection.reset(
            graph.getElements().filter((element) => {
              if (typeof selection.options.filter === "function") {
                return !selection.options.filter(element);
              }
            })
          );
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "ctrl+plus command+plus": (evt: any) => {
          evt.preventDefault();
          paperScroller.zoom(0.2, { max: 5, grid: 0.1 });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "ctrl+minus command+minus": (evt: any) => {
          evt.preventDefault();
          paperScroller.zoom(-0.2, { min: 0.2, grid: 0.1 });
        },

        "keydown:alt": () => {
          paperScroller.setCursor("grab");
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete: (evt: any) => {
          evt.preventDefault();
          graph.removeCells(selection.collection.toArray(), {
            mergeArrays: true,
          });
          selection.collection.reset([]);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        backspace: (evt: any) => {
          evt.preventDefault();
          graph.removeCells(selection.collection.toArray(), {
            mergeArrays: true,
          });
        },
        "keydown:shift": () => {
          paperScroller.setCursor("crosshair");
        },
        "keyup:shift": () => {
          paperScroller.setCursor("grab");
        },
        "keydown:esc": () => {
          selection.cancelSelection();
        },
      },
      keyboard
    );
  }

  disable() {
    const { keyboard } = this.context;
    keyboard.disable();
  }

  enable() {
    const { keyboard } = this.context;
    keyboard.enable();
    this.activateKeyboard();
  }

  getDeepSelection() {
    const { selection } = this.context;
    const deepEmbeds = flatten(selection.collection.map(this.getDeepEmbeds));
    return new joint.mvc.Collection([...selection.collection.toArray(), ...deepEmbeds]);
  }

  private hasTextSelection(): boolean {
    const sel = window.getSelection();
    return !!sel && sel.toString().length > 0;
  }

  getDeepEmbeds(cell: joint.dia.Cell) {
    return cell.getEmbeddedCells({ deep: true });
  }

  private paste(pastedCells: joint.dia.Cell[]) {
    const { selection, graph } = this.context;

    if (selection.collection.length === 1) {
      // pasting into element - embed into target element
      const target = selection.collection.at(0);
      const pasteBBox = graph.getCellsBBox(pastedCells, { deep: true });
      const translate = {
        dx: target.getBBox().x - pasteBBox.x + 20,
        dy: target.getBBox().y - pasteBBox.y + 20,
      };
      pastedCells
        .filter((c) => !c.isEmbedded())
        .forEach((c) => {
          if (c instanceof joint.dia.Element) {
            (c as joint.dia.Element).translate(translate.dx, translate.dy);
          }
          target.embed(c);
        });
    } else if (selection.collection.length === 0 && activeView.value !== null) {
      // Pasting regions into subdiagram - embed into parent state
      const parentState = activeView.value.model;
      pastedCells
        .filter((c) => !c.isEmbedded())
        .forEach((c) => {
          if (c instanceof Region) {
            parentState.embed(c);
          }
        });
    }
  }

  private canPaste(): { allowed: boolean; message?: string } {
    const { clipboard, selection } = this.context;

    // multiple elements selected - don't paste
    if (selection.collection.length > 1) {
      return {
        allowed: false,
        message: "Cannot paste with multiple elements selected.",
      };
    }

    // no element selected at top level - paste
    if (selection.collection.length === 0 && activeView.value === null) {
      return { allowed: true };
    }

    // no element selected in subdiagram - only allow if pasting Regions
    if (selection.collection.length === 0 && activeView.value !== null) {
      if (!clipboard.isEmpty()) {
        const allRegions = clipboard.models.filter((c) => !c.get("parent")).every((c) => c.get("type") === "Region");

        if (allRegions) {
          return { allowed: true };
        }
      }
      return {
        allowed: false,
        message:
          "Cannot paste in subdiagram without selecting a target element. Only Regions can be pasted directly into subdiagrams.",
      };
    }

    // one element selected - only paste if it can be embedded
    if (selection.collection.length === 1) {
      const target = selection.collection.at(0);
      if (!clipboard.isEmpty()) {
        const canEmbedAll = clipboard.models
          .filter((c) => !c.get("parent"))
          .every((c) => {
            const cellType = c.get("type");
            const isRegion = cellType === "Region";
            const isParentRegionWithNonRegionChild = target instanceof Region && !isRegion;
            const isParentStateWithRegionChild = target instanceof State && isRegion;
            return target.attributes["embedable"] && (isParentRegionWithNonRegionChild || isParentStateWithRegionChild);
          });

        if (!canEmbedAll) {
          return {
            allowed: false,
            message: "Cannot paste because States can only embed Regions, and Regions cannot embed other Regions.",
          };
        }
        return { allowed: true };
      }
    }

    return { allowed: false, message: "Cannot paste at this location." };
  }
}
